<template>
  <div id="reportPage">
    <main>
      <article>
        <div v-if="isLoading" class="pw-loading-overlay" aria-live="polite" aria-busy="true">
          <div class="pw-loading-card">
            <div class="pw-spinner" />
            <div class="pw-loading-text">Loading…</div>
          </div>
        </div>
        <div class="title-wrapper mb-4">
        <h2 class="login-title">WEEKLY ORDER</h2>
          <UserMenu :roles="roles" :user-name="userName" />
        </div>
          <div class="table-responsive">
          <table class="table table-bordered align-middle">
            <thead class="table-light">
              <tr>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>WEEK</span>
                    <button
                      type="button"
                      ref="weekPickerAnchor"
                      class="filter-icon-btn active"
                      @click.stop="toggleWeekPicker"
                      title="Select week"
                    >
                      <font-awesome-icon icon="filter" />
                      <span class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>CLIENT</span>
                    <button
                      type="button"
                      ref="clientFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: clientFilterActive }"
                      @click.stop="toggleClientFilterPopover"
                      title="Filter by client"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="clientFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>COMPLETED TRANSFERS</span>
                    <button
                      type="button"
                      ref="achievedFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: achievedFilterActive }"
                      @click.stop="toggleAchievedFilterPopover"
                      title="Filter by completed transfers"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="achievedFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>WEEKLY ORDER</span>
                    <button
                      type="button"
                      ref="pointFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: pointFilterActive }"
                      @click.stop="togglePointFilterPopover"
                      title="Filter by Weekly Order"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="pointFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="center">REVENUE</th>
                <th class="center">COMPLETION RATE</th>
                <th class="center">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in filteredRows" :key="i">
                <td>{{ row.week }}</td>
                <td>{{ row.client }}</td>
                <td>{{ row.count }}</td>
                <td>{{ row.total_points }}</td>
                <td class="center" :title="revenueTitle(row)">{{ formatMoney(rowRevenue(row)) }}</td>
                <td>
                  {{
                    Number(row.total_points) > 0 && !isNaN(Number(row.count))
                      ? ((Number(row.count) / Number(row.total_points)) * 100).toFixed(2) + '%'
                      : '0%'
                  }}
                </td>
                <td class="td-center col-actions">
                    <div class="actions-wrapper">
                      <button
                        class="dots-btn"
                        @click.stop="toggleActions(row.client)"
                        aria-label="Actions"
                      >
                        ⋮
                      </button>

                      <div
                        v-if="openActionsId === row.client"
                        class="actions-popover"
                      >
                        <button
                          class="popover-btn"
                          @click="openEditFromMenu(row.client)"
                        >
                          ✏ Edit
                        </button>
                      </div>
                    </div>
                </td>
              </tr>
              <tr v-if="rows.length === 0">
                <td colspan="7" class="text-center py-3">No results</td>
              </tr>
            </tbody>
            <tfoot v-if="filteredRows.length">
              <tr class="pw-totals-row">
                <td colspan="2"><strong>Total</strong></td>
                <td><strong>{{ weekTotals.completed }}</strong></td>
                <td><strong>{{ weekTotals.ordered }}</strong></td>
                <td class="center">
                  <div class="pw-total-revenue-label">Total Weekly Revenue</div>
                  <strong>{{ formatMoney(weekTotals.revenue) }}</strong>
                </td>
                <td><strong>{{ weekTotals.rate }}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div class="d-flex justify-content-center align-items-center gap-2 mt-3">
          <button
            class="btn btn-sm btn-outline-primary"
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          >
            ◀ Prev
          </button>

          <span>
            Page {{ currentPage }} of {{ totalPages }}
          </span>

          <button
            class="btn btn-sm btn-outline-primary"
            :disabled="currentPage >= totalPages"
            @click="changePage(currentPage + 1)"
          >
            Next ▶
          </button>
        </div>

      </article>

      <!-- Column filter popovers -->
      <Teleport to="body">
        <div
          v-if="showClientFilterModal"
          ref="clientFilterPopover"
          class="pw-filter-popover"
          :style="clientFilterPopoverStyle"
        >
          <div class="pw-filter-header">
            <span class="pw-filter-title">Filter by Client</span>
            <button type="button" class="pw-filter-close" @click="closeClientFilterModal">×</button>
          </div>
          <div class="pw-filter-body">
            <input v-model="clientFilterTemp" type="text" class="pw-filter-search" placeholder="Client (contains)..." />
          </div>
          <div class="pw-filter-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearClientFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyClientFilter">Apply</button>
          </div>
        </div>
        <div
          v-if="showAchievedFilterModal"
          ref="achievedFilterPopover"
          class="pw-filter-popover"
          :style="achievedFilterPopoverStyle"
        >
          <div class="pw-filter-header">
            <span class="pw-filter-title">Filter by Completed</span>
            <button type="button" class="pw-filter-close" @click="closeAchievedFilterModal">×</button>
          </div>
          <div class="pw-filter-body">
            <input v-model="achievedFilterTemp" type="text" class="pw-filter-search" placeholder="Value (contains)..." />
          </div>
          <div class="pw-filter-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearAchievedFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyAchievedFilter">Apply</button>
          </div>
        </div>
        <div
          v-if="showPointFilterModal"
          ref="pointFilterPopover"
          class="pw-filter-popover"
          :style="pointFilterPopoverStyle"
        >
          <div class="pw-filter-header">
            <span class="pw-filter-title">Filter by Weekly</span>
            <button type="button" class="pw-filter-close" @click="closePointFilterModal">×</button>
          </div>
          <div class="pw-filter-body">
            <input v-model="pointFilterTemp" type="text" class="pw-filter-search" placeholder="Value (contains)..." />
          </div>
          <div class="pw-filter-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearPointFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyPointFilter">Apply</button>
          </div>
        </div>
      </Teleport>

      <!-- Week picker teleported to body to avoid table layout issues -->
      <Teleport to="body">
        <div
          v-if="weekPickerOpen"
          ref="weekList"
          class="week-picker-dropdown"
          :style="weekPickerStyle"
          @click.stop
          @scroll="onDropdownScroll"
        >
          <div
            v-for="(item, idx) in weekItems"
            :key="idx"
            class="week-picker-item"
            :class="{ active: item.offset === weekOffset }"
            @click="selectWeekItem(item)"
          >
            {{ item.label }}
          </div>
        </div>
      </Teleport>

      <div v-if="showCreateModal" class="modal-overlayy" @click="closeCreateModal">
        <div class="modal-contentt" @click.stop>
          
          <!-- Заголовок -->
          <div class="transfers-modal-header">
            <div>
              <div class="transfers-modal-client">{{ editingClient }}</div>
              <div class="transfers-modal-week">{{ filters.week }}</div>
            </div>
            <div class="transfers-modal-total">
              <span class="transfers-total-label">Weekly Order</span>
              <span class="transfers-total-value">{{ editingClientTotalTransfers }}</span>
            </div>
          </div>

          <!-- Последние добавления -->
          <div class="transfers-history-section">
            <div class="transfers-history-title">Recent adjustments</div>
            <div v-if="editingClientHistory.length === 0" class="transfers-history-empty">
              No Weekly Order adjustments this week yet
            </div>
            <div v-else class="transfers-history-list">
              <div
                v-for="h in [...editingClientHistory].reverse()"
                :key="h.id"
                class="transfers-history-item"
              >
                <div class="transfers-history-left">
                  <span
                    class="transfers-history-badge"
                    :class="{ 'transfers-history-badge--deduct': Number(h.points) < 0 }"
                  >{{ Number(h.points) >= 0 ? '+' : '' }}{{ h.points }}</span>
                  <span class="transfers-history-by">by {{ h.added_by }}</span>
                </div>
                <div class="transfers-history-right">
                  <span class="transfers-history-date">{{ h.created_at }}</span>
                  <button
                    v-if="canReverseHistoryEntry(h)"
                    type="button"
                    class="btn btn-sm btn-outline-danger transfers-reverse-btn"
                    @click="reverseHistoryEntry(h)"
                  >
                    Reverse
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="transfers-divider"></div>

          <!-- Форма корректировки Weekly Order -->
          <div class="transfers-add-section">
            <label class="transfers-add-label">Adjust Weekly Order</label>
            <div class="transfers-adjust-mode" role="group" aria-label="Adjustment type">
              <button
                type="button"
                class="btn btn-sm"
                :class="transfersAdjustmentMode === 'add' ? 'btn-primary' : 'btn-outline-primary'"
                @click="transfersAdjustmentMode = 'add'"
              >
                Add
              </button>
              <button
                type="button"
                class="btn btn-sm"
                :class="transfersAdjustmentMode === 'deduct' ? 'btn-danger' : 'btn-outline-danger'"
                @click="transfersAdjustmentMode = 'deduct'"
              >
                Deduct
              </button>
            </div>
            <div class="transfers-add-row">
              <input
                v-model.number="transfersToAdd"
                type="number"
                min="1"
                :max="transfersAdjustmentMode === 'deduct' ? editingClientTotalTransfers : null"
                class="form-control transfers-add-input"
                placeholder="e.g. 50"
              />
              <button
                type="button"
                class="btn transfers-add-btn"
                :class="transfersAdjustmentMode === 'deduct' ? 'btn-danger' : 'btn-primary'"
                @click="submitTransfers"
              >
                {{ transfersAdjustmentMode === 'deduct' ? 'Deduct' : 'Add' }}
              </button>
            </div>
          </div>

          <button type="button" class="btn btn-sm btn-link transfers-close-btn" @click="closeCreateModal">
            Close
          </button>

        </div>
      </div>
    </main>
  </div>
</template>

<script>
import UserMenu from '@/components/UserMenu.vue'
export default {
  components: { UserMenu },
  data() {
    return {
      isLoading: false,
      transfersToAdd: 0,
      transfersAdjustmentMode: 'add',
      weekOffset: 0,
      editingClient: null,
      weekPickerOpen: false,
      weekPickerStyle: {},
      weekPickerClickOutside: null,
      clientFilter: "",
      clientFilterTemp: "",
      showClientFilterModal: false,
      clientFilterPopoverStyle: {},
      clientFilterClickOutside: null,
      achievedFilter: "",
      achievedFilterTemp: "",
      showAchievedFilterModal: false,
      achievedFilterPopoverStyle: {},
      achievedFilterClickOutside: null,
      pointFilter: "",
      pointFilterTemp: "",
      showPointFilterModal: false,
      pointFilterPopoverStyle: {},
      pointFilterClickOutside: null,
      weekItems: [],
      isViewMode: false,
      refundReason: '',
      autoRefreshInterval: null,
      refreshRate: 0, 
      rows: [],
      summary: {},
      campaignOptions: [],
      insuranceOptions: [],
      openActionsId: null,
      totalItems: 0,
      showCreateModal: false,
      totalPages: 1, 
      currentPage: 1,
      editingClientHistory: [],
      editingClientTotalTransfers: 0,
      pageSize: 100,
      userName: localStorage.getItem("username") || "User",
      roles: [],
      audioVisible: {},
      audioPlaying: {},
      reversingIds: {},
      filters: {
        campaign_name: "",
        phone_number: "",
        state: "",
        status:"",
        status_name: "",
        call_duration:"",
        first_name:"",
        last_name:"",
        email:"",
        custom1:"",
        custom2:"",
        custom3:"",
        date_of_birth:"",
        length_in_sec: "",
        date_from: "",
        date_to: ""
      },
      weekDropdownOpen: false,
      weekOptions: [
        { label: "This Week", value: "thisWeek" },
        { label: "Last Week", value: "lastWeek" },
        { label: "Week Before Last", value: "weekBeforeLast" },
        // при необходимости можно добавить больше
      ],
    };
  },
  computed: {
    filteredRows() {
      const q = (v) => (v || "").trim().toLowerCase();
      const c = q(this.clientFilter);
      const a = q(this.achievedFilter);
      const p = q(this.pointFilter);
      return this.rows.filter(row => {
        if (c && !String(row.client || "").toLowerCase().includes(c)) return false;
        if (a && !String(row.count ?? "").includes(a)) return false;
        if (p && !String(row.total_points ?? "").includes(p)) return false;
        return true;
      });
    },
    clientFilterActive() { return this.clientFilter.trim() !== ""; },
    achievedFilterActive() { return this.achievedFilter.trim() !== ""; },
    pointFilterActive() { return this.pointFilter.trim() !== ""; },
    weekTotals() {
      const rows = this.filteredRows || [];
      const completed = rows.reduce((sum, row) => sum + Number(row.count || 0), 0);
      const ordered = rows.reduce((sum, row) => sum + Number(row.total_points || 0), 0);
      const revenue = rows.reduce((sum, row) => sum + this.rowRevenue(row), 0);
      const rate = ordered > 0 ? ((completed / ordered) * 100).toFixed(2) + "%" : "0%";
      return { completed, ordered, revenue, rate };
    },
    isAdmin() {
      return Array.isArray(this.roles) && this.roles.includes("admin");
    }
  },
  beforeUnmount() {
    this.removeWeekPickerClickOutside();
    this.removeClientFilterClickOutside();
    this.removeAchievedFilterClickOutside();
    this.removePointFilterClickOutside();
  },
  mounted() {
    this.applyWeekOffset();

    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return;
    }

    this.fetchData();
    this.check_role();
    document.title = "DNQ Report";

    this.fpInstance = flatpickr(".daterange", {
      enableTime: true,
      time_24hr: true,
      minuteIncrement: 5,
      dateFormat: "Y-m-d H:i",
      mode: "range",
      onReady: (selectedDates, dateStr, instance) => {
        // Контейнер для кнопок
        const btnContainer = document.createElement("div");
        btnContainer.className = "fp-quick-range-buttons d-flex gap-2 p-2";

        // Диапазоны
        const ranges = [
            { label: "This Week", value: "thisWeek" },
            { label: "Last Week", value: "lastWeek" },
            { label: "Week Before Last", value: "weekBeforeLast" },
          ];

        ranges.forEach(r => {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.textContent = r.label;
          btn.className = "btn btn-sm btn-outline-primary"; // Bootstrap стиль
          btn.style.padding = "2px 6px"; // меньше, чем у btn-sm по умолчанию
          btn.style.fontSize = "12px";   // чуть меньше шрифт
          btn.addEventListener("click", () => this.setQuickRange(r.value));
          btnContainer.appendChild(btn);
        });

        // Вставляем внутрь календаря
        instance.calendarContainer.appendChild(btnContainer);
      },
      onClose: (selectedDates) => {
        if (selectedDates.length === 2) {
          // Keep selected range in local time (avoid UTC shift from toISOString()).
          this.filters.date_from = this._formatLocalDateTime(selectedDates[0], false);
          this.filters.date_to   = this._formatLocalDateTime(selectedDates[1], true);
        } else {
          this.filters.date_from = "";
          this.filters.date_to = "";
        }
        this.currentPage = 1;
        this.fetchData();
      }
    });
  },

  methods: {
    _pad2(n) {
      return String(n).padStart(2, "0");
    },
    _formatLocalDate(d) {
      if (!(d instanceof Date)) return "";
      return `${d.getFullYear()}-${this._pad2(d.getMonth() + 1)}-${this._pad2(d.getDate())}`;
    },
    _formatLocalDateTime(d, endOfDay = false) {
      const date = this._formatLocalDate(d);
      if (!date) return "";
      return endOfDay ? `${date} 23:59:59` : `${date} 00:00:00`;
    },
    toggleWeekDropdown() {
    this.weekDropdownOpen = !this.weekDropdownOpen;
  },
  onWeekScroll(e) {
    e.stopPropagation();
    const direction = e.deltaY > 0 ? 1 : -1;
    
    if (this._scrollTimeout) clearTimeout(this._scrollTimeout);
    this._scrollTimeout = setTimeout(() => {
      this.shiftWeek(direction);
    }, 50);
  },

  shiftWeek(direction) {
    this.weekOffset += direction;
    this.applyWeekOffset();
  },

 applyWeekOffset() {
      const today = new Date();
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1) - this.weekOffset * 7;
      
      const start = new Date(today);
      start.setDate(diff);
      start.setHours(0, 0, 0, 0);
      
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      end.setHours(23, 59, 59, 999);

      const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      this.filters.week = `${fmt(start)} – ${fmt(end)}`;
      // IMPORTANT: keep week boundaries in *local time* (avoid UTC shift from toISOString()).
      this.filters.date_from = this._formatLocalDateTime(start, false);
      this.filters.date_to = this._formatLocalDateTime(end, true);
      this.currentPage = 1;
    },
    onTopDateChange(value) {
    if (value && value.length === 2) {
      this.filters.date_from = this._formatLocalDateTime(value[0], false);
      this.filters.date_to = this._formatLocalDateTime(value[1], true);
    } else {
      this.filters.date_from = "";
      this.filters.date_to = "";
    }
    this.currentPage = 1;
    this.fetchData();
  },
    setQuickRange(range) {
      const today = new Date();
      let from = null;
      let to = null;

      // Вспомогательная функция для вычисления начала и конца недели (понедельник - воскресенье)
      const getWeekRange = (offsetWeeks = 0) => {
        const curr = new Date(today);
        curr.setDate(curr.getDate() - curr.getDay() + 1 - offsetWeeks * 7); // Понедельник недели
        const start = new Date(curr);
        start.setHours(0, 0, 0, 0);
        const end = new Date(curr);
        end.setDate(end.getDate() + 6); // Воскресенье недели
        end.setHours(23, 59, 59, 999);
        return { start, end };
      };

      switch (range) {
        case 'today':
          from = new Date(today.setHours(0, 0, 0, 0));
          to = new Date(today.setHours(23, 59, 59, 999));
          break;
        case 'yesterday':
          const yest = new Date(today);
          yest.setDate(yest.getDate() - 1);
          from = new Date(yest.setHours(0, 0, 0, 0));
          to = new Date(yest.setHours(23, 59, 59, 999));
          break;
        case 'thisWeek':
          ({ start: from, end: to } = getWeekRange(0));
          break;
        case 'lastWeek':
          ({ start: from, end: to } = getWeekRange(1));
          break;
        case 'weekBeforeLast':
          ({ start: from, end: to } = getWeekRange(2));
          break;
        case 'thisMonth':
          from = new Date(today.getFullYear(), today.getMonth(), 1);
          to = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
          break;
        case 'lastMonth':
          from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          to = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999);
          break;
      }

      if (from && to) {
        this.filters.date_from = this._formatLocalDateTime(from, false);
        this.filters.date_to = this._formatLocalDateTime(to, true);

        if (this.fpInstance) {
          this.fpInstance.setDate([from, to], true);
        }

        this.currentPage = 1;
        this.fetchData();
      }
    },
    toggleActions(id) {
      this.openActionsId = this.openActionsId === id ? null : id
    },
    toggleWeekPicker() {
      if (this.weekPickerOpen) {
        this.closeWeekPicker();
        return;
      }
      this.generateWeekItems(this.weekOffset);
      this.weekPickerOpen = true;
      this.$nextTick(() => {
        this.positionWeekPicker();
        const list = this.$refs.weekList;
        if (list) {
          const active = list.querySelector('.active');
          if (active) active.scrollIntoView({ block: 'center' });
        }
      });
      this.addWeekPickerClickOutside();
    },
    closeWeekPicker() {
      this.weekPickerOpen = false;
      this.removeWeekPickerClickOutside();
    },
    positionWeekPicker() {
      const anchor = this.$refs.weekPickerAnchor;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const popoverWidth = 200;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.weekPickerStyle = {
        position: 'fixed',
        top: `${rect.bottom + 4}px`,
        left: `${left}px`,
        zIndex: '9999'
      };
    },
    addWeekPickerClickOutside() {
      this.removeWeekPickerClickOutside();
      setTimeout(() => {
        this.weekPickerClickOutside = (e) => {
          const anchor = this.$refs.weekPickerAnchor;
          const list = this.$refs.weekList;
          if (anchor && anchor.contains(e.target)) return;
          if (list && list.contains(e.target)) return;
          this.closeWeekPicker();
        };
        document.addEventListener('click', this.weekPickerClickOutside);
      }, 0);
    },
    removeWeekPickerClickOutside() {
      if (this.weekPickerClickOutside) {
        document.removeEventListener('click', this.weekPickerClickOutside);
        this.weekPickerClickOutside = null;
      }
    },

    // ── Client filter ──
    toggleClientFilterPopover() {
      this.showClientFilterModal ? this.closeClientFilterModal() : this.openClientFilterModal();
    },
    openClientFilterModal() {
      this.closeAchievedFilterModal(); this.closePointFilterModal();
      this.clientFilterTemp = this.clientFilter;
      this.showClientFilterModal = true;
      this.$nextTick(() => this._positionPopover('clientFilterBtn', 'clientFilterPopoverStyle', 280));
      this._addClickOutside('clientFilter', 'clientFilterPopover', 'clientFilterBtn', 'closeClientFilterModal');
    },
    closeClientFilterModal() {
      this.showClientFilterModal = false;
      this.removeClientFilterClickOutside();
    },
    applyClientFilter() { this.clientFilter = (this.clientFilterTemp || "").trim(); this.closeClientFilterModal(); },
    clearClientFilter() { this.clientFilter = ""; this.clientFilterTemp = ""; this.closeClientFilterModal(); },
    removeClientFilterClickOutside() { this._removeClickOutside('clientFilter'); },

    // ── Achieved filter ──
    toggleAchievedFilterPopover() {
      this.showAchievedFilterModal ? this.closeAchievedFilterModal() : this.openAchievedFilterModal();
    },
    openAchievedFilterModal() {
      this.closeClientFilterModal(); this.closePointFilterModal();
      this.achievedFilterTemp = this.achievedFilter;
      this.showAchievedFilterModal = true;
      this.$nextTick(() => this._positionPopover('achievedFilterBtn', 'achievedFilterPopoverStyle', 280));
      this._addClickOutside('achievedFilter', 'achievedFilterPopover', 'achievedFilterBtn', 'closeAchievedFilterModal');
    },
    closeAchievedFilterModal() {
      this.showAchievedFilterModal = false;
      this.removeAchievedFilterClickOutside();
    },
    applyAchievedFilter() { this.achievedFilter = (this.achievedFilterTemp || "").trim(); this.closeAchievedFilterModal(); },
    clearAchievedFilter() { this.achievedFilter = ""; this.achievedFilterTemp = ""; this.closeAchievedFilterModal(); },
    removeAchievedFilterClickOutside() { this._removeClickOutside('achievedFilter'); },

    // ── Point filter ──
    togglePointFilterPopover() {
      this.showPointFilterModal ? this.closePointFilterModal() : this.openPointFilterModal();
    },
    openPointFilterModal() {
      this.closeClientFilterModal(); this.closeAchievedFilterModal();
      this.pointFilterTemp = this.pointFilter;
      this.showPointFilterModal = true;
      this.$nextTick(() => this._positionPopover('pointFilterBtn', 'pointFilterPopoverStyle', 280));
      this._addClickOutside('pointFilter', 'pointFilterPopover', 'pointFilterBtn', 'closePointFilterModal');
    },
    closePointFilterModal() {
      this.showPointFilterModal = false;
      this.removePointFilterClickOutside();
    },
    applyPointFilter() { this.pointFilter = (this.pointFilterTemp || "").trim(); this.closePointFilterModal(); },
    clearPointFilter() { this.pointFilter = ""; this.pointFilterTemp = ""; this.closePointFilterModal(); },
    removePointFilterClickOutside() { this._removeClickOutside('pointFilter'); },

    // ── Shared helpers ──
    _positionPopover(btnRef, styleKey, width) {
      const btn = this.$refs[btnRef];
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      let left = rect.left;
      if (left + width > window.innerWidth) left = Math.max(8, window.innerWidth - width - 8);
      this[styleKey] = { position: 'fixed', top: `${rect.bottom + 4}px`, left: `${left}px`, zIndex: '9999' };
    },
    _addClickOutside(key, popoverRef, btnRef, closeFn) {
      this._removeClickOutside(key);
      setTimeout(() => {
        const handler = (e) => {
          const popover = this.$refs[popoverRef];
          const btn = this.$refs[btnRef];
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this[closeFn]();
        };
        this[`${key}ClickOutside`] = handler;
        document.addEventListener('click', handler);
      }, 0);
    },
    _removeClickOutside(key) {
      const prop = `${key}ClickOutside`;
      if (this[prop]) {
        document.removeEventListener('click', this[prop]);
        this[prop] = null;
      }
    },

    generateWeekItems(centerOffset) {
      const items = [];
      for (let i = centerOffset + 20; i >= centerOffset - 20; i--) {
        items.push({
          offset: i,
          label: this.getWeekLabel(i)
        });
      }
      this.weekItems = items;
    },

    getWeekLabel(offset) {
      const today = new Date();
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1) - offset * 7;
      const start = new Date(today);
      start.setDate(diff);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${fmt(start)} – ${fmt(end)}`;
    },

    onDropdownScroll() {
      const list = this.$refs.weekList;
      if (!list) return;
      const items = this.weekItems;

      if (list.scrollTop + list.clientHeight >= list.scrollHeight - 40) {
        const lastOffset = items[items.length - 1].offset;
        for (let i = 1; i <= 10; i++) {
          items.push({ offset: lastOffset - i, label: this.getWeekLabel(lastOffset - i) });
        }
      }

      // Скроллим вверх — подгружаем более давние недели (больший offset)
      if (list.scrollTop <= 40) {
        const firstOffset = items[0].offset;
        const scrollBefore = list.scrollHeight;
        for (let i = 10; i >= 1; i--) {
          items.unshift({ offset: firstOffset + i, label: this.getWeekLabel(firstOffset + i) });
        }
        this.$nextTick(() => {
          list.scrollTop += list.scrollHeight - scrollBefore;
        });
      }
    },

    selectWeekItem(item) {
      this.weekOffset = item.offset;
      this.applyWeekOffset();
      this.weekPickerOpen = false;
      // Вызываем fetchData после выбора недели
      this.fetchData();
    },
    openEditFromMenu(client) {
      this.openActionsId = null
      this.openEditModal(client)
    },
    rowRevenue(row) {
      if (!row) return 0;
      if (row.revenue !== undefined && row.revenue !== null && row.revenue !== "") {
        return Number(row.revenue) || 0;
      }
      const completed = Number(row.count || 0);
      const price = Number(row.price_per_transfer ?? row.pricePerTransfer ?? 0);
      return Math.round(completed * price * 100) / 100;
    },
    revenueTitle(row) {
      const price = Number(row?.price_per_transfer ?? row?.pricePerTransfer ?? 0);
      const completed = Number(row?.count || 0);
      if (!price) return "Set Price per Transfer in the customer profile";
      return `${completed} × ${this.formatMoney(price)}`;
    },
    formatMoney(value) {
      const n = Number(value || 0);
      return n.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
     closeCreateModal() {
      this.showCreateModal = false;
      this.transfersToAdd = 0;
      this.transfersAdjustmentMode = 'add';
      this.editingClient = null;
    },
    async submitTransfers() {
      const amount = Number(this.transfersToAdd || 0);
      if (!amount || amount <= 0) {
        alert('Please enter a valid Weekly Order adjustment');
        return;
      }
      const isDeduction = this.transfersAdjustmentMode === 'deduct';
      if (isDeduction && amount > Number(this.editingClientTotalTransfers || 0)) {
        alert(`Deduction cannot exceed the current Weekly Order (${this.editingClientTotalTransfers})`);
        return;
      }
      const signedPoints = isDeduction ? -amount : amount;

      const token = localStorage.getItem('token');
      const username = localStorage.getItem('username');

      try {
        const res = await fetch('/api/calllogs/add_points', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            client: this.editingClient,
            points: signedPoints,
            note: isDeduction ? 'deduction' : '',
            week_start: this.filters.date_from.slice(0, 10),
            week_end: this.filters.date_to.slice(0, 10),
            username: username
          })
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(payload.error || 'Failed to adjust Weekly Order');
        }
        const entry = payload?.entry || null;

        // Добавляем в локальную историю сразу — без перезагрузки
        if (entry && entry.id) {
          this.editingClientHistory.push(entry);
        }
        this.editingClientTotalTransfers = Number(
          payload.total_points ?? (Number(this.editingClientTotalTransfers || 0) + signedPoints)
        );
        this.transfersToAdd = 0;

        // Обновляем таблицу в фоне
        this.fetchData();

      } catch (e) {
        console.error('Error adjusting Weekly Order:', e);
        alert(e?.message || 'Error adjusting Weekly Order');
      }
    },

    canReverseHistoryEntry(h) {
      if (!this.isAdmin) return false;
      if (!h || !h.id) return false;
      const points = Number(h.points || 0);
      if (points <= 0) return false;
      const note = String(h.note || "");
      if (note.trim().startsWith("reversal_of_id=")) return false;
      return true;
    },

    async reverseHistoryEntry(h) {
      if (!this.canReverseHistoryEntry(h)) return;
      const token = localStorage.getItem('token');
      const username = localStorage.getItem('username');
      this.reversingIds = { ...(this.reversingIds || {}), [h.id]: true };
      try {
        const res = await fetch('/api/calllogs/reverse_points', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            historyId: h.id,
            username
          })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Failed to reverse transfers');
        }

        const entry = data.entry || null;
        // Remove the original entry from "Recent additions" after reversal.
        this.editingClientHistory = (this.editingClientHistory || []).filter(x => x.id !== h.id);
        // Reversal entries are hidden from history by backend policy,
        // so don't push the reversal entry into the local list.
        if (entry) {
          this.editingClientTotalTransfers -= Number(entry.points || 0);
        }
        this.fetchData();
      } catch (e) {
        console.error('Error reversing transfers:', e);
        alert(e?.message || 'Error reversing transfers');
      } finally {
        this.reversingIds = { ...(this.reversingIds || {}), [h.id]: false };
      }
    },

    openEditModal(client) {
      this.editingClient = client;
      this.transfersToAdd = 0;
      this.transfersAdjustmentMode = 'add';

      const row = this.rows.find(r => r.client === client);
      this.editingClientHistory = row ? (row.points_history || []) : [];
      this.editingClientTotalTransfers = row ? (row.total_points || 0) : 0;

      this.showCreateModal = true;
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.fetchData();
    },
    async check_role(){
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ user: this.userName });
      const res = await fetch(`/api/role?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      this.roles = data.roles;
      return this.roles
    },
    async fetchData() {
      console.log("fetch");
      const token = localStorage.getItem("token");

      if (!token) {
        console.error("Token is missing or expired.");
        this.$router.push('/login');
        return;
      }

      this.isLoading = true;
      const cleanFilters = {};
      Object.keys(this.filters).forEach(key => {
        cleanFilters[key] =
          this.filters[key] === null || this.filters[key] === undefined
            ? ''
            : this.filters[key];
      });

      const params = new URLSearchParams({
        page: this.currentPage,
        page_size: this.pageSize,
        ...cleanFilters
      });

      const username = localStorage.getItem('username');
      if (!this.filters.date_from) {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        this.filters.date_from = this._formatLocalDateTime(todayStart, false);
      }

      if (!this.filters.date_to) {
        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);
        this.filters.date_to = this._formatLocalDateTime(todayEnd, true);
      }

      // Если роль admin, исключаем targetNumber
      const requestData = {
        reportStart: this.filters.date_from,
        reportEnd: this.filters.date_to
      };
      const roless = Array.from(this.roles);
      try {
        const res = await fetch(`/api/calllogs/weekly`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(requestData)
        });

        if (!res.ok) {
          const text = await res.text();
          console.error(`Error: ${res.status} - ${text}`);
          if (res.status === 401) {
            localStorage.removeItem('token');
            this.$router.push('/login');
            return;
          }
          // При ошибке устанавливаем пустые данные
          this.rows = [];
          this.totalItems = 0;
          this.totalPages = 1;
          return;
        }

        const data = await res.json();

        if (!data || !Array.isArray(data.records)) {
          console.error("Invalid response format:", data);
          this.rows = [];
          this.totalItems = 0;
          this.totalPages = 1;
          return;
        }

        // Безопасное преобразование данных
        this.rows = (data.records || []).map(call => ({
            ...call,
            week: this.filters.week || call.week || '-',
            refundRequestCreated: call.refunds && call.refunds.length > 0,
            existingRefunds: call.refunds || []
          }));
        
        this.totalItems = data.total || this.rows.length;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize) || 1;

        return data;
      } catch (e) {
        console.error("Fetch error:", e);
        // При ошибке устанавливаем пустые данные, чтобы страница не сломалась
        this.rows = [];
        this.totalItems = 0;
        this.totalPages = 1;
      } finally {
        this.isLoading = false;
      }
    }
  },
  watch: {
    refreshRate() {
      this.startAutoRefresh();
    },
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1;
        this.fetchData();
      }
    },
  }
};
</script>

<style scoped>

.daterange {
  cursor: pointer;
  background-color: #fff;
}
#reportPage {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
  padding: 20px 10px;
}

#reportPage main {
  display: flex;
  justify-content: center;
  width: 100%;
}

#reportPage article {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: none;
  position: relative;
}

.pw-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.pw-loading-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.pw-spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  border-top-color: #4e54c8;
  animation: pw-spin 0.8s linear infinite;
}

.pw-loading-text {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

@keyframes pw-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.login-title {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #4e54c8;
}

.panel-action-btn {
  background: linear-gradient(135deg, #6a11cb, #2575fc);
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  padding: 10px 20px;
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 150px;
  max-width: 150px;
  height: 106.59px;
  flex-shrink: 0;
  margin-left: auto;
}

.panel-action-btn:hover {
  transform: translateY(-2px);
}

.panel-action-btn:active {
  transform: translateY(1px);
}

.btn-submit {
  width: 200px;
  max-width: 100%;
  display: inline-block;
  background: linear-gradient(135deg, #6a11cb, #2575fc);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease, transform 0.2s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.btn-submit:hover {
  background: linear-gradient(135deg, #5a0fc1, #1e63d6);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
}

.btn-submit:active {
  transform: translateY(1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.summary-panels-wrapper {
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}


.summary-panels {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  flex: 1;
}

.datepicker-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.datepicker-wrapper .calendar-icon {
  position: absolute;
  right: 10px;
  color: #4e54c8;
  cursor: pointer;
}

.datepicker-wrapper input {
  padding-right: 30px;
  border-radius: 12px;
  border: 1px solid #ccc;
  font-size: 12px;
  height: 32px;
}

.datepicker-wrapper input:focus {
  border-color: #4e54c8;
  box-shadow: 0 0 0 2px rgba(78, 84, 200, 0.2);
}


.panel {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 110px;
  background: #f0f4ff;
  padding: 10px 15px;
  border-radius: 8px;
  text-align: center;
  flex: 1 1 150px;
  min-width: 150px;
  max-width: 150px;
}

.panel-title {
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
}

.panel-value {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
}
.v-select {
  font-size: 12px;
  border-radius: 8px;
}

.v-select .vs__dropdown-toggle {
  min-height: 32px;
}

.v-select .vs__selected {
  font-size: 12px;
}


.table-responsive table {
  min-width: 700px;
  -webkit-overflow-scrolling: touch;
  table-layout: fixed;
  width: 100%
}

table {
  width: 100%;
  table-layout: auto;
  font-size: 10px;
}

.pw-totals-row td {
  background: #f1f5f9;
  border-top: 2px solid #cbd5e1;
  font-size: 11px;
}

.pw-total-revenue-label {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 2px;
}

th, td {
  word-wrap: break-word;
  vertical-align: middle !important;
}

.filter-input,
.v-select .vs__dropdown-toggle {
  height: 32px;
  min-height: 32px;
  font-size: 10px;
  padding: 6px 8px;
}

.multiselect-sm {
  font-size: 10px;
}

.multiselect-sm .vs__dropdown-toggle {
  min-height: 32px;
  height: 32px;
  font-size: 10px;
  border-radius: 8px;
  border: 1px solid #ced4da;
  padding: 0 6px;
  background-color: #fff;
}

.multiselect-sm .vs__selected {
  font-size: 10px;
  line-height: 32px;
  padding: 0 4px;
}

.multiselect-sm .vs__placeholder {
  font-size: 10px;
  line-height: 32px;
  color: #6c757d;
}

.multiselect-sm .vs__dropdown-menu {
  font-size: 10px;
}

.multiselect-sm .vs__search {
  font-size: 10px;
  line-height: 32px;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-responsive table {
  min-width: 1000px;
  width: 100%;
  table-layout: auto;
}

@media (max-width: 768px) {
  .table-responsive table {
    min-width: 700px;
  }
  th, td {
    white-space: nowrap;
  }
}
.multiselect-sm .vs__dropdown-toggle,
.multiselect-sm .vs__actions,
.multiselect-sm .vs__search {
  border-radius: 10px;
}


@media (max-width: 768px) {
  .login-title { font-size: 22px; }
  table { font-size: 12px; }
  .filter-input { font-size: 11px; padding: 4px 6px; }
  .panel-action-btn { max-width: 100%; padding: 8px 16px; height: 110px; }
  .summary-panels-container { gap: 10px; }
}
.pie-chart-container {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.chart-title {
  margin-bottom: 15px;
  font-size: 18px;
  font-weight: 600;
  color: #4e54c8;
}

.summary-panels {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  flex-shrink: 0;
  align-items: stretch;
}

.panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #f0f4ff;
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  flex: 1 1 150px;
  min-height: 150px;
}
.summary-panels-container {
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  gap: 20px;
}

.pie-chart-container.no-card {
  background: transparent;
  padding: 0;
  box-shadow: none;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.summary-panels-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}
.quick-range-buttons {
  display: flex;
  gap: 5px;
  margin-top: 5px;
}


.quick-range-buttons button {
  padding: 3px 6px;
  font-size: 11px;
  border-radius: 5px;
  border: 1px solid #ccc;
  background: #f0f0f0;
  cursor: pointer;
}

.quick-range-buttons button:hover {
  background: #e0e0ff;
}

.fp-quick-range-buttons {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  padding: 5px 10px;
  border-top: none;
  justify-content: flex-start;
}

.fp-quick-range-buttons .fp-quick-btn {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #6a11cb, #2575fc);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 3px 8px rgba(0,0,0,0.15);
  transition: all 0.3s ease;
}

.fp-quick-range-buttons .fp-quick-btn:hover {
  background: linear-gradient(135deg, #5a0fc1, #1e63d6);
  transform: translateY(-1px);
  box-shadow: 0 5px 12px rgba(0,0,0,0.2);
}

.fp-quick-range-buttons .fp-quick-btn:active {
  transform: translateY(1px);
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}
.table-top-filters {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.datepicker-top {
  max-width: 220px;
}

.quick-range-buttons button {
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 5px;
  border: 1px solid #ccc;
  background: #f0f0f0;
  cursor: pointer;
}

.quick-range-buttons button:hover {
  background: #e0e0ff;
}

.table-top-filters {
  position: relative;
  overflow: visible;
}
.week-filter {
  position: relative;
}

.week-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 9999;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
}

.week-option {
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s ease;
  white-space: nowrap;
}

.week-option:hover {
  background: #f0f4ff;
  color: #4e54c8;
}

.actions-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

/* СИНИЕ ТРОЕТОЧИЕ, БЕЗ ВСЕГО */
.dots-btn {
  background: none;
  border: none;
  padding: 4px 6px;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #4f46e5;
}

.dots-btn:hover {
  color: #4338ca;
}

.actions-popover {
  position: absolute;
  top: 50%;
  right: 100%;
  transform: translate(-8px, -50%);
  background: #ffffff;
  border-radius: 10px;
  padding: 6px;
  min-width: 150px;
  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  z-index: 20;
}

/* КНОПКИ ВНУТРИ */
.popover-btn {
  width: 100%;
  background: none;
  border: none;
  padding: 10px 12px;
  font-size: 14px;
  text-align: left;
  border-radius: 8px;
  cursor: pointer;
  color: #111827;
}
.modal-overlayy {
  position: fixed; /* фиксированное по экрану */
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}
.modal-contentt {
  background: white;
  border-radius: 8px;
  padding: 20px;
  width: 500px;
  max-width: 90%;
  max-height: 90%;
  overflow-y: auto;
  box-shadow: 0 4px 15px rgba(0,0,0,0.25);
}
.large-textarea {
  width: 100%;
  min-height: 150px;
  padding: 12px;
  border-radius: 8px;
  resize: vertical;
  font-size: 14px;
}

.week-carousel {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ns-resize;
  padding: 4px 8px;
  border-radius: 6px;
  user-select: none;
}

.week-carousel:hover {
  background: #f0f4ff;
}

.week-label {
  font-size: 11px;
  min-width: 120px;
  text-align: center;
}
.week-picker-wrapper {
  position: relative;
}

.week-picker-dropdown {
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  width: 200px;
  height: 252px;
  overflow-y: auto;
}

.week-picker-item {
  padding: 9px 12px;
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  height: 36px;
  display: flex;
  align-items: center;
}

.week-picker-item:hover {
  background: #f0f4ff;
  color: #4e54c8;
}

.week-picker-item.active {
  background: #4e54c8;
  color: white;
  font-weight: 600;
}

.transfers-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.transfers-modal-client {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
}

.transfers-modal-week {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

.transfers-modal-total {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.transfers-total-label {
  font-size: 11px;
  color: #888;
}

.transfers-total-value {
  font-size: 28px;
  font-weight: 800;
  color: #4e54c8;
  line-height: 1;
}

.transfers-history-section {
  margin-bottom: 16px;
}

.transfers-history-title {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.transfers-history-empty {
  font-size: 12px;
  color: #bbb;
  padding: 10px 0;
}

.transfers-history-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 4px;
}

.transfers-history-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9ff;
  border-radius: 8px;
  padding: 8px 12px;
}

.transfers-history-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.transfers-history-badge {
  background: #4e54c8;
  color: white;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 13px;
  font-weight: 700;
}

.transfers-history-badge--deduct {
  background: #dc3545;
}

.transfers-history-by {
  font-size: 12px;
  color: #555;
}

.transfers-history-date {
  font-size: 11px;
  color: #aaa;
}

.transfers-history-right {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.transfers-reverse-btn {
  padding: 2px 8px;
  font-size: 11px;
  line-height: 1.2;
}

.transfers-divider {
  height: 1px;
  background: #eee;
  margin: 16px 0;
}

.transfers-add-section { display: block; }

.transfers-add-label {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 8px;
}

.transfers-adjust-mode {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.transfers-add-row {
  display: flex;
  gap: 8px;
}

.transfers-add-input {
  flex: 1;
  font-size: 16px;
  font-weight: 600;
}

.transfers-add-btn {
  padding: 6px 20px;
  font-weight: 600;
  background: linear-gradient(135deg, #4e54c8, #2575fc);
  border: none;
  border-radius: 8px;
}

.transfers-add-btn.btn-danger {
  background: #dc3545;
}

.transfers-close-btn {
  display: block;
  width: 100%;
  text-align: center;
  margin-top: 12px;
  color: #aaa;
  font-size: 12px;
}

/* ── Column filter icons ── */
.campaign-header-cell {
  white-space: nowrap;
}
.campaign-header-cell-inner {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.campaign-header-cell-inner span {
  font-weight: 600;
}
.filter-icon-btn {
  position: relative;
  padding: 2px 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}
.filter-icon-btn:hover { color: #4e54c8; }
.filter-icon-btn.active { color: #4e54c8; }
.filter-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  font-size: 9px;
  font-weight: 700;
  color: #fff;
  background: #4e54c8;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* ── Filter popovers ── */
.pw-filter-popover {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  border: 1px solid #e0e0e0;
  width: 280px;
  display: flex;
  flex-direction: column;
}
.pw-filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
}
.pw-filter-title {
  font-size: 13px;
  font-weight: 600;
}
.pw-filter-close {
  background: none;
  border: none;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: #999;
  padding: 0 4px;
}
.pw-filter-close:hover { color: #333; }
.pw-filter-body { padding: 10px 12px; }
.pw-filter-search {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  box-sizing: border-box;
}
.pw-filter-footer {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid #eee;
}
</style>